import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BooleanSelect from "@/components/onboarding/shared/BooleanSelect";
import { FieldTooltip } from "@/components/onboarding/shared/FieldTooltip";
import { ONBOARDING_TOOLTIPS } from "@/utils/onboardingTooltips";
import type {
  EntityData,
  RelatieStatRomanData,
  ReprezentantRomania,
} from "@/types/entity";

type EntityStateRelationSectionProps = {
  entityData: Partial<EntityData>;
  onFieldChange: (
    field: keyof RelatieStatRomanData,
    value: boolean | null
  ) => void;
  onReprezentantChange: (
    field: keyof ReprezentantRomania,
    value: string
  ) => void;
  fieldErrors: Record<string, string>;
};

type BooleanRelatieField = Extract<
  keyof RelatieStatRomanData,
  | "stabilita_in_romania"
  | "sediu_principal_in_romania"
  | "entitate_administratie_publica"
  | "entitate_critica_294_2024"
  | "entitate_extra_ue"
>;

type BooleanQuestion = {
  field: BooleanRelatieField;
  label: string;
  tooltip: string;
};

const QUESTIONS: BooleanQuestion[] = [
  {
    field: "stabilita_in_romania",
    label: "Entitatea este stabilită în România?",
    tooltip: ONBOARDING_TOOLTIPS.stabilita_in_romania,
  },
  {
    field: "sediu_principal_in_romania",
    label: "Entitatea are sediul principal în România?",
    tooltip: ONBOARDING_TOOLTIPS.sediu_principal_in_romania,
  },
  {
    field: "entitate_administratie_publica",
    label:
      "Entitatea dumneavoastră este o entitate a administrației publice înființată de România?",
    tooltip: ONBOARDING_TOOLTIPS.entitate_administratie_publica,
  },
  {
    field: "entitate_critica_294_2024",
    label:
      "Entitatea este/a fost identificată ca entitate critică în România (conform dispozițiilor Legii nr. 294/2024)?",
    tooltip: ONBOARDING_TOOLTIPS.entitate_critica_294_2024,
  },
  {
    field: "entitate_extra_ue",
    label:
      "Entitatea este stabilită în afara UE și are un reprezentant desemnat în România?",
    tooltip: ONBOARDING_TOOLTIPS.entitate_extra_ue,
  },
];

function FieldError({ error }: { error: string | undefined }) {
  if (!error) return null;
  return (
    <div className="flex items-center gap-2 mt-1 px-4">
      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
      <span className="text-xs text-destructive">{error}</span>
    </div>
  );
}

function EntityStateRelationSection({
  entityData,
  onFieldChange,
  onReprezentantChange,
  fieldErrors,
}: EntityStateRelationSectionProps) {
  const data = entityData.relatie_stat_roman;
  const showReprezentant = data?.entitate_extra_ue === true;
  const rep = data?.reprezentant_romania;

  return (
    <div className="py-2 space-y-4">
      <div className="border border-ds-border-200 rounded-lg overflow-hidden divide-y divide-ds-border-100">
        {QUESTIONS.map(({ field, label, tooltip }) => (
          <div key={field}>
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <span className="text-xs md:text-sm text-ds-text-600 flex-1 flex items-center gap-1">
                {label}
                <FieldTooltip content={tooltip} />
              </span>
              <div className="w-32 shrink-0">
                <BooleanSelect
                  value={data?.[field] ?? null}
                  onChange={(val) => onFieldChange(field, val)}
                  variant="neutral"
                />
              </div>
            </div>
            <FieldError error={fieldErrors[`relatie_stat_roman.${field}`]} />
          </div>
        ))}
      </div>

      {showReprezentant && (
        <div className="border border-ds-border-200 rounded-lg p-4 space-y-3">
          <Label className="text-sm font-medium block">
            Date reprezentant în România
          </Label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-ds-text-500">
                Denumire reprezentant{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="ex: SC Reprezentant SRL"
                value={rep?.denumire ?? ""}
                onChange={(e) =>
                  onReprezentantChange("denumire", e.target.value)
                }
                className="h-8 text-sm"
              />
              <FieldError
                error={
                  fieldErrors[
                  "relatie_stat_roman.reprezentant_romania.denumire"
                  ]
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-ds-text-500">
                Adresă în România <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="ex: Str. Exemplu 1, București"
                value={rep?.adresa ?? ""}
                onChange={(e) => onReprezentantChange("adresa", e.target.value)}
                className="h-8 text-sm"
              />
              <FieldError
                error={
                  fieldErrors["relatie_stat_roman.reprezentant_romania.adresa"]
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-ds-text-500">Email (opțional)</Label>
              <Input
                placeholder="ex: contact@reprezentant.ro"
                value={rep?.email ?? ""}
                onChange={(e) => onReprezentantChange("email", e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-ds-text-500">
                Telefon (opțional)
              </Label>
              <Input
                placeholder="ex: +40 721 000 000"
                value={rep?.telefon ?? ""}
                onChange={(e) =>
                  onReprezentantChange("telefon", e.target.value)
                }
                className="h-8 text-sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EntityStateRelationSection;
